import { useState } from 'react';

const thumb = (id: string, size: 'hq' | 'maxres') =>
  `https://i.ytimg.com/vi/${id}/${size === 'hq' ? 'hqdefault' : 'maxresdefault'}.jpg`;

/**
 * A YouTube video that shows only its thumbnail until it is played, so the page
 * doesn't load YouTube's player (about 1 MB) for visitors who never press play.
 */
export default function YouTubeVideo({ id, title, duration, poster, autoPlay = false, large = false, className = '' }: {
  id: string; title: string; duration?: string; poster?: string; autoPlay?: boolean; large?: boolean; className?: string;
}) {
  const [playing, setPlaying] = useState(autoPlay);
  const [src, setSrc] = useState(poster ?? thumb(id, large ? 'maxres' : 'hq'));

  return (
    <div className={`relative aspect-video overflow-hidden rounded-[16px] bg-navy-deep ${className}`}>
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          // The site sends Referrer-Policy: same-origin; YouTube refuses embeds with no referrer (error 153).
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 h-full w-full border-0"
        />
      ) : (
        <button type="button" onClick={() => setPlaying(true)} aria-label={`Play video: ${title}`}
          className="group absolute inset-0 block h-full w-full cursor-pointer">
          <img src={src} alt="" loading="lazy" onError={() => setSrc(thumb(id, 'hq'))}
            className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]" />
          <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-navy-deep/70 via-transparent to-transparent" />
          <span aria-hidden
            className={`absolute left-1/2 top-1/2 grid -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-accent text-white shadow-[0_12px_30px_-8px_rgba(255,132,75,.8)] transition group-hover:scale-110 ${large ? 'h-20 w-20' : 'h-14 w-14'}`}>
            <svg viewBox="0 0 24 24" className={large ? 'ml-1 h-9 w-9' : 'ml-0.5 h-6 w-6'}><path fill="currentColor" d="M7 4.5v15a1 1 0 0 0 1.53.85l12-7.5a1 1 0 0 0 0-1.7l-12-7.5A1 1 0 0 0 7 4.5Z" /></svg>
          </span>
          {duration && (
            <span className="absolute bottom-3 right-3 rounded-[8px] bg-navy-deep/85 px-2 py-0.5 font-mono text-[12px] font-semibold text-white">
              {duration}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
